/// Port of mobile/lib/game/high_score_label.dart.
///
/// Amber trophy + score with a white highlight band sliding across, drawn
/// above the dino while waiting to start.
///
/// Two Flutter details need web equivalents:
/// - `canvas.saveLayer` + `BlendMode.srcIn` becomes an offscreen canvas with
///   `globalCompositeOperation = 'source-in'`, which is the same operation.
/// - The trophy SVG is 13 flat rects on a 24x24 grid at exactly the size the
///   label draws it, so they are emitted directly rather than rasterised.

import { PositionComponent } from './engine/component';
import { anchors } from './engine/vector';
import { appColors } from '../app/theme/appColors';
import { formatScore } from '../utils/formatScore';

/// x, y, width, height on the 24x24 grid of assets/pixel_icons/trophy.svg.
const trophyRects: readonly (readonly [number, number, number, number])[] = [
  [6, 3, 12, 2],
  [6, 3, 2, 12],
  [16, 3, 2, 12],
  [16, 5, 6, 2],
  [2, 5, 6, 2],
  [2, 7, 2, 4],
  [4, 11, 2, 2],
  [18, 11, 2, 2],
  [20, 7, 2, 4],
  [8, 15, 8, 2],
  [11, 17, 2, 4],
  [9, 19, 6, 2],
];

interface HighScoreLabelDeps {
  isWaiting: () => boolean;
  isDarkMode: () => boolean;
  dinoPosition: () => { x: number; y: number };
  dinoSize: () => { x: number; y: number };
}

export class HighScoreLabel extends PositionComponent {
  static readonly iconSize = 24.0;
  static readonly gap = 4.0;
  static readonly fontSize = 24.0;
  static readonly shimmerPeriod = 1.5;

  private score = 0;
  private shimmerT = 0;

  private readonly layer: HTMLCanvasElement;
  private readonly layerCtx: CanvasRenderingContext2D;
  private readonly deps: HighScoreLabelDeps;

  constructor(deps: HighScoreLabelDeps, initialScore = 0) {
    super({ anchor: anchors.bottomCenter, size: { x: 72, y: 20 }, priority: 20 });
    this.deps = deps;
    this.score = initialScore;

    this.layer = document.createElement('canvas');
    const context = this.layer.getContext('2d');
    if (!context) throw new Error('highScoreLabel: 2d context unavailable');
    this.layerCtx = context;
  }

  setScore(score: number): void {
    this.score = score;
  }

  override update(dt: number): void {
    if (!this.deps.isWaiting()) return;

    this.shimmerT += dt;

    const dinoPosition = this.deps.dinoPosition();
    const dinoSize = this.deps.dinoSize();
    this.position = {
      x: dinoPosition.x + dinoSize.x / 2,
      y: dinoPosition.y - dinoSize.y - 8,
    };
  }

  override render(ctx: CanvasRenderingContext2D, originX: number, originY: number): void {
    const label = formatScore(this.score);
    const font = `${HighScoreLabel.fontSize}px "Jersey 10", monospace`;

    ctx.save();
    ctx.font = font;
    ctx.letterSpacing = '0.5px';
    const textWidth = ctx.measureText(label).width;
    ctx.restore();

    const trophySize = HighScoreLabel.iconSize;
    const contentW = trophySize + HighScoreLabel.gap + textWidth;
    const contentH = Math.max(trophySize, HighScoreLabel.fontSize);

    // As in the Dart original, the measured size lands one frame late: the
    // anchor offset baked into originX was computed from the previous size.
    this.size = { x: contentW, y: contentH };

    const width = Math.max(1, Math.ceil(contentW));
    const height = Math.max(1, Math.ceil(contentH));
    if (this.layer.width !== width || this.layer.height !== height) {
      this.layer.width = width;
      this.layer.height = height;
    }

    const layerCtx = this.layerCtx;
    layerCtx.clearRect(0, 0, width, height);
    layerCtx.globalCompositeOperation = 'source-over';
    layerCtx.imageSmoothingEnabled = false;

    // Opaque mask only - the real colour comes from the shimmer gradient.
    layerCtx.fillStyle = '#ffffff';
    const trophyY = (contentH - trophySize) / 2;
    const scale = trophySize / 24;
    for (const [rx, ry, rw, rh] of trophyRects) {
      layerCtx.fillRect(rx * scale, trophyY + ry * scale, rw * scale, rh * scale);
    }

    layerCtx.font = font;
    layerCtx.letterSpacing = '0.5px';
    layerCtx.textBaseline = 'middle';
    layerCtx.fillText(label, trophySize + HighScoreLabel.gap, contentH / 2);

    // Same slide maths as package:shimmer (ltr).
    const percent = (this.shimmerT / HighScoreLabel.shimmerPeriod) % 1.0;
    const dx = -contentW + 2 * contentW * percent;
    const gold = this.deps.isDarkMode()
      ? appColors.darkHighScoreGold
      : appColors.lightHighScoreGold;
    const gradient = layerCtx.createLinearGradient(dx - contentW, 0, dx + 2 * contentW, 0);
    gradient.addColorStop(0.0, gold);
    gradient.addColorStop(0.35, gold);
    gradient.addColorStop(0.5, '#ffffff');
    gradient.addColorStop(0.65, gold);
    gradient.addColorStop(1.0, gold);

    layerCtx.globalCompositeOperation = 'source-in';
    layerCtx.fillStyle = gradient;
    layerCtx.fillRect(0, 0, width, height);

    ctx.drawImage(this.layer, originX, originY);
  }
}
